import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { BetValidationData } from '@/feature/betslip-pickem/api/types';
import { FantasyEntryPromotionStatus } from '@/feature/betslip-pickem/hooks/use-fantasy-entry-promotions';
import { BetslipPick, PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { SharedEntryPicks } from '@/feature/entry-share/api/types';
import { nonRegularProjectionTypes } from '@/feature/lobby/utils/filters';
import { CreateEntryError } from '@/hooks/use-entries';
import { ProductConfigKeys, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import {
    Pick as ApiPick,
    BoostedMultiplier,
    Currency,
    DynamicMultiplier,
    EntryError,
    EntryErrorType,
    EntryRulesOutput,
    GameMode,
    MetadataType,
    Outcome,
    PromotionType,
} from '@/types/api.generated';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { PICK_TYPE_LABEL } from '@/utils/constants';
import { EntryErrorCodes } from '@/utils/errors/entry-errors';
import { logger } from '@/utils/logging';
import { formatNumber } from '@/utils/numeric/format';

import { EntryAmountValidationData } from '../hooks/use-betslip-actions';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { eventUtils } from './event-utils';

export const mapPicksToApiData = (picks: BetslipPick[]): ApiPick[] => {
    return picks.map(pick => {
        const selectedOption = pick.projection.allowedOptions?.find(it => it.outcome === pick.outcome);
        const option = selectedOption
            ? { marketOptionId: selectedOption?.marketOptionId, outcome: selectedOption?.outcome }
            : undefined;
        return {
            eventId: pick.eventId,
            marketId: pick.projection.marketId,
            projection: {
                name: pick.projection.name,
                value: pick.projection.value,
                isLive: pick.projection.isLive,
                option,
                type: pick.projection.type,
                nonRegularPercentage: pick.projection.nonRegularPercentage,
                nonRegularValue: pick.projection.nonRegularValue,
            },
            league: pick.player.league,
            outcome: pick.outcome,
            sport: pick.player.sport,
            teamId: pick.player.team?.id,
            projectionName: pick.projection.name,
            projectionValue: pick.projection.value,
            playerId: pick.player.id,
        };
    });
};

export const mapPlayerAndEvents = (sortedBetslip: BetslipPick[], events?: EventInfo[]): PlayerWithTeamAndEvent[] => {
    const playersList = sortedBetslip
        .map(pick => {
            const event = events?.find(ev => ev.id === pick.eventId);
            if (!event) {
                return null;
            }
            return {
                ...pick.player,
                event,
                sport: pick.player.sport,
                league: pick.player.league,
            };
        })
        .filter(Boolean);

    return playersList;
};

export const getInvalidPicksError = (data: BetValidationData) => {
    return data?.errors?.find(
        err => err.code === EntryErrorCodes.INVALID_PROJECTION || err.code === EntryErrorCodes.INVALID_EVENT_STATUS
    );
};

export const hasInvalidProjections = (e: CreateEntryError) =>
    e.response?.error?.code === EntryErrorCodes.INVALID_PROJECTION;

export const containsRestrictionErrors = (data: BetValidationData): boolean => {
    return !!data.errors.find(it => it.type === EntryErrorType.Restriction);
};

export const containsMultipleRestrictionErrors = (restrictionErrors: EntryError) => {
    return restrictionErrors.restrictedPlayerIdPairs && restrictionErrors.restrictedPlayerIdPairs.length > 1;
};

export const containsSingleRestrictionError = (restrictionErrors: EntryError) => {
    return restrictionErrors.restrictedPlayerIdPairs && restrictionErrors.restrictedPlayerIdPairs.length === 1;
};

export const getRestrictedCombinationErrorMsg = (errors: EntryError[]): string => {
    return errors
        .filter(err => err.type === EntryErrorType.Restriction)
        .map(err => err.message)
        .join('\n');
};

export const getExceedsMaxEdgeCombosErrorMsg = (data: BetValidationData): string | undefined => {
    const specific = data.errors.find(
        it =>
            it.code === EntryErrorCodes.MAX_EDGE_COMBOS_NUMBER_SURPASSED_FOR_LEAGUE ||
            it.code === EntryErrorCodes.MAX_EDGE_COMBOS_NUMBER_SURPASSED_FOR_PROJECTION
    );
    if (specific) {
        return specific.message;
    }
    return data.errors.find(it => it.code === EntryErrorCodes.MAX_EDGE_COMBOS_NUMBER_SURPASSED)?.message;
};

/**
 * There are cases where an entry is invalid and BE returns 0 multipliers or 0 toWin.
 * In those cases, we want to hide the payout details in the UI.
 * eg: when a user exceeds the max allowed multiplier (2250x)
 */
export const hasMultiplierExceededError = (data?: BetValidationData) => {
    if (data?.errors.find(it => it.code === EntryErrorCodes.MAX_MULTIPLIER_EXCEEDED)) {
        return true;
    }
    return false;
};

export const getWinningPicksAndToWin = (
    data: DynamicMultiplier[],
    dynamicBoostedMultiplier?: BoostedMultiplier
): {
    winningPicksRange: string;
    highestToWin: number;
    highestMultiplier: number;
    lowestMultiplier: number;
    multiplierRange: string;
} => {
    if (data.length === 0) {
        return {
            winningPicksRange: '',
            highestToWin: 0,
            highestMultiplier: 0,
            lowestMultiplier: 0,
            multiplierRange: '',
        };
    }

    const winningPicksArray = data.map(item => item.winningPicks);
    const minWinningPicks = Math.min(...winningPicksArray);
    const maxWinningPicks = Math.max(...winningPicksArray);
    const highestMultiplier = dynamicBoostedMultiplier?.multiplier ?? Math.max(...data.map(item => item.multiplier));
    const lowestMultiplier = Math.min(...data.map(item => item.multiplier));
    const multiplierRange = `${lowestMultiplier}-${highestMultiplier}`;

    const highestToWin = dynamicBoostedMultiplier?.toWin ?? Math.max(...data.map(item => item.toWin));

    const winningPicksRange = `${minWinningPicks}-${maxWinningPicks}`;

    return { winningPicksRange, highestToWin, highestMultiplier, lowestMultiplier, multiplierRange };
};

export const getBoostedMultiplierDetails = (
    shouldShowMultiplier: boolean,
    dynamicMultiplier: DynamicMultiplier,
    dynamicBoostedMultiplier?: BoostedMultiplier
) => {
    if (!shouldShowMultiplier) {
        return { boostedMultiplier: undefined, toWin: dynamicMultiplier.toWin };
    }
    return {
        boostedMultiplier: dynamicBoostedMultiplier?.multiplier,
        toWin: dynamicBoostedMultiplier?.toWin ?? dynamicMultiplier.toWin,
    };
};

export const mapValidationDataToGameModes = (
    data: BetValidationData[],
    entryRules: EntryRulesOutput[],
    limitsValidation: Record<GameMode, EntryAmountValidationData>
) => {
    const result: Record<GameMode, { betValidation?: BetValidationData; amountValidation: EntryAmountValidationData }> =
        {
            DYNAMIC: {
                betValidation: undefined,
                amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
            },
            PERFECT: {
                betValidation: undefined,
                amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
            },
            MYSTERY: {
                betValidation: undefined,
                amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
            },
            RACE_TO_PLACE: {
                betValidation: undefined,
                amountValidation: { errorMessage: undefined, validEntry: false, maxAllowedEntryAmount: 0 },
            },
        };

    data.forEach(it => {
        const entryRule = entryRules.find(er => er.id === it.entryRulesId);
        if (entryRule?.gameMode) {
            result[entryRule.gameMode] = {
                betValidation: it,
                amountValidation: limitsValidation?.[entryRule.gameMode],
            };
        } else {
            logger.error('[BetValidation] No entry rule for:', it);
        }
    });

    return {
        ...result,
        DEFAULT: result.PERFECT ?? data[0],
    };
};

export const calculateDollarPlaceholderFilling = (winningPicks: number, maxWinningPicks: number) => {
    const maxFilling = 3;
    const diff = maxWinningPicks - winningPicks;
    return maxFilling - diff;
};

export const mapFeedEntryToBetslipPick = (entryPicks: SocialFeedEntry['picks'], events: EventInfo[]): BetslipPick[] => {
    return (
        entryPicks?.map(pick => {
            const event = events.find(ev => ev.id === pick.eventId);
            const team = eventUtils.getTeamById(event, pick.teamId);
            const players = isTeamEvent(event) ? team?.players : event?.players;
            const projections = players?.find(player => player.id === pick.player.id)?.projections ?? [];
            let teamBetSlip;

            if (team) {
                const { players: _, ...teamWithoutPlayers } = team;
                teamBetSlip = teamWithoutPlayers;
            }

            return {
                eventId: pick.eventId,
                player: {
                    ...pick.player,
                    team: teamBetSlip,
                    sport: pick.sport,
                    league: pick.league,
                    projections: projections,
                },
                projection: pick.projection,
                outcome: pick.outcome,
            };
        }) ?? []
    );
};

export const mapPicksToBetSlipPick = (pickOutputs: SharedEntryPicks, events: EventInfo[]): BetslipPick[] => {
    return pickOutputs.map(pick => {
        const event = events.find(ev => ev.id === pick.eventId);
        const team = eventUtils.getTeamById(event, pick.teamId);
        const players = isTeamEvent(event) ? team?.players : event?.players;
        const projections = players?.find(player => player.id === pick.player.id)?.projections ?? [];
        let teamBetSlip;

        if (team) {
            const { players: _, ...teamWithoutPlayers } = team;
            teamBetSlip = teamWithoutPlayers;
        }

        return {
            eventId: pick.eventId,
            player: {
                ...pick.player,
                team: teamBetSlip,
                sport: pick.sport,
                league: pick.league,
                projections: projections,
            },
            projection: pick.projection,
            outcome: pick.outcome,
        };
    });
};

type PartialBetslipPick = {
    eventId: BetslipPick['eventId'];
    outcome: BetslipPick['outcome'];
    player: Pick<BetslipPick['player'], 'id'>;
    projection: Pick<BetslipPick['projection'], 'type'>;
};

const isSamePick = (a: PartialBetslipPick, b: PartialBetslipPick): boolean =>
    a.player.id === b.player.id &&
    a.eventId === b.eventId &&
    a.projection.type === b.projection.type &&
    a.outcome === b.outcome;

export const getMatchingBetslipPicks = (betslip: BetslipPick[], picks: PartialBetslipPick[]): BetslipPick[] =>
    betslip.filter(betslipPick => picks.some(pick => isSamePick(betslipPick, pick)));

export const isPrebuiltLineupInBetslip = (betslip: BetslipPick[], picks: PartialBetslipPick[]): boolean =>
    picks.every(pick => betslip.some(betslipPick => isSamePick(pick, betslipPick)));

/**
 * @deprecated
 * Avoid using this method, as it's often used in UI and does not properly respond to state updates.
 * Look at `betslipPlayerSelector` instead.
 */
export const isPlayerSelected = ({ eventId, playerId }: { eventId?: string; playerId?: string }) => {
    return useBetslipStore.getState().betslip.find(item => item.eventId === eventId && item.player.id === playerId);
};

export const getErrorForGameMode = (gameMode: GameMode, perfectModeError?: string, dynamicModeError?: string) => {
    switch (gameMode) {
        case GameMode.Perfect:
            return perfectModeError;
        case GameMode.Dynamic:
            return dynamicModeError;
        default:
            return undefined;
    }
};

export const getPickslipDefaultEntryAmount = (
    defaultAmount: number,
    cash: number,
    betrBucks: number,
    maxAllowedEntryAmount: number,
    userLimits?: number[]
): { amount: number; currency: Currency } => {
    const limit = userLimits && userLimits.length > 0 ? userLimits[0] : Infinity;

    const effectiveDefaultAmount = Math.min(defaultAmount, limit, maxAllowedEntryAmount);
    if (cash >= 1) {
        return { amount: Math.min(effectiveDefaultAmount, cash), currency: Currency.Usd };
    }
    return { amount: Math.min(effectiveDefaultAmount, betrBucks), currency: Currency.Fre };
};

export const getPickInfo = (outcome: Outcome, projection: PlayerProjectionFragment): string => {
    return `${PICK_TYPE_LABEL[outcome]} ${formatNumber(
        nonRegularProjectionTypes.includes(projection.type) ? projection.nonRegularValue : projection.value
    )} ${projection.label}`;
};

export const getPromotionTypeLabel = (promo: FantasyEntryPromotionStatus): string => {
    const type = promo.promotion.type;

    if (type === 'MYSTERY') {
        return promo.promotion.name;
    }

    if (type === PromotionType.PlayAndGet) {
        const amount = promo.promotion.metadata.find(dataType => dataType.type === MetadataType.CurrentAmount)?.value;
        const target = promo.promotion.metadata.find(dataType => dataType.type === MetadataType.TargetAmount)?.value;
        const currentAmount = amount ? parseFloat(amount).toString() : '0';
        const targetAmount = target ?? '0';

        return `Play & Get: $${currentAmount} / $${targetAmount} played`;
    }

    return promo.promotion.name;
};

export const isFreeEntryPromo = (promo?: FantasyEntryPromotionStatus): boolean =>
    promo?.promotion.type === PromotionType.FreeEntry;

/**
 * Returns the exact entry amount a FREE_ENTRY promo forces (BONUS_AMOUNT metadata), or undefined for other promos.
 */
export const getFreeEntryBonusAmount = (promo?: FantasyEntryPromotionStatus): number | undefined => {
    if (!promo || !isFreeEntryPromo(promo)) {
        return undefined;
    }
    const bonusAmount = promo.promotion.metadata.find(data => data.type === MetadataType.BonusAmount)?.value;
    const amount = Number(bonusAmount);
    return bonusAmount && !isNaN(amount) ? amount : undefined;
};

/**
 * Returns the game modes available from [Perfect, Dynamic] for the current jurisdiction.
 */
export const filterEntryRules = (allEntryRules: EntryRulesOutput[]) => {
    const dynamicPlay = allEntryRules.find(rule => rule.gameMode === GameMode.Dynamic);
    const perfectPlay = allEntryRules.find(rule => rule.gameMode === GameMode.Perfect);
    return [perfectPlay, dynamicPlay].filter(Boolean);
};

export const getDefaultEntryAmount = (
    entryCurrency: Currency,
    wallet: { betrBucks: number; realMoneyTotal: number },
    entryMaxAllowed: number
): number => {
    const prismicConfiguredDefaultAmount =
        useJurisdictionStore.getState().jurisdictionSettings?.productConfig?.keyValuePairs?.[
            ProductConfigKeys.PickslipConfig
        ]?.default_entry_amount ?? '20';

    const walletMax = entryCurrency === Currency.Fre ? wallet.betrBucks : wallet.realMoneyTotal;
    return Math.min(parseInt(prismicConfiguredDefaultAmount, 10), walletMax, entryMaxAllowed);
};
